'use client';

import styled from '@emotion/styled';
import { useEffect, useMemo, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import type { INewsItemEntry } from '../../../../contentful';
import { INewsletterEntry, IPressReleaseEntry } from '../../../../contentful';
import {
	ArrowButton,
	ContentWrap,
	contentWrapBreakpoint,
	Css,
	IDropdownItem,
	Responsive,
	StyledSelectMain,
} from '../../../../ui';
import { paginate } from '../../../../utils/paginate';
import { Color } from '../../../../styles/Color';
import { SelectInput } from '../../../../components/common/select/SelectInput';
import { SearchInput } from '../../../../components/common/search-input/SearchInput';

import {
	getAllCategoryLabels,
	getSelectableYears,
	makeFilter,
} from '../domain/utils/helpers';
import { PageRoutes } from '../../../app/pages/domain/constants/PageRoutes';
import NewsroomPagination from './Pagination';
import { NewsType } from '../domain/types/NewsType';
import NewsroomOverviewList from './NewsroomOverviewList';
import { MultiSelectInput } from '../../../../components/common/select/MultiSelectInput';

interface IProps {
	pageNumber: number;
	itemsPerPage?: number;
	items: (INewsItemEntry | INewsletterEntry | IPressReleaseEntry)[];
	root: PageRoutes;
	type: NewsType;
}

interface IFiltersType {
	selectedYear: string;
	queryText: string;
	selectedCategories: IDropdownItem[];
}

const ITEMS_PER_PAGE = 12;
const ALL_YEARS_FILTER_RESET = 'All years';

export default function NewsroomOverviewWrapper({
	items,
	pageNumber,
	itemsPerPage = ITEMS_PER_PAGE,
	root,
	type,
}: IProps) {
	const router = useRouter();
	const searchParams = useSearchParams();

	const [searchValue, setSearchValue] = useState('');
	const [filters, setFilters] = useState<IFiltersType>({
		selectedYear: '',
		queryText: '',
		selectedCategories: [],
	});

	const newsItems = useMemo(
		() => items.filter((item) => item.contentType === 'newsArticle'),
		[items],
	);
	const allCategoryLabels = [
		...getAllCategoryLabels(newsItems as INewsItemEntry[]),
	];

	const categoriesKey = useMemo(
		() => filters.selectedCategories.join('|'),
		[filters.selectedCategories],
	);

	useEffect(() => {
		const categoryParams = searchParams.get('category')?.split(',') || [];
		setFilters({
			selectedYear: searchParams.get('year') || '',
			queryText: searchParams.get('search') || '',
			selectedCategories: categoryParams,
		});
		setSearchValue(searchParams.get('search') || '');
	}, [searchParams.toString()]);

	const filterFn = useMemo(
		() =>
			makeFilter(
				filters.queryText,
				filters.selectedYear || ALL_YEARS_FILTER_RESET,
				ALL_YEARS_FILTER_RESET,
				filters.selectedCategories,
			),
		[filters.queryText, filters.selectedYear, categoriesKey],
	);

	const filteredItems = useMemo(
		() => items.filter(filterFn),
		[items, filterFn],
	);

	const { numberOfPages, getPageByNumber } = useMemo(
		() => paginate(filteredItems, itemsPerPage),
		[filteredItems, itemsPerPage],
	);

	const listData = getPageByNumber(pageNumber);

	const updateFilters = (newFilters: Partial<typeof filters>) => {
		const updatedFilters = { ...filters, ...newFilters };

		// If filters didn't change, return to prevent unnecessary re-render
		if (JSON.stringify(updatedFilters) === JSON.stringify(filters)) return;

		setFilters(updatedFilters);

		const params = new URLSearchParams();

		if (
			updatedFilters.selectedYear &&
			updatedFilters.selectedYear !== ALL_YEARS_FILTER_RESET
		) {
			params.set('year', updatedFilters.selectedYear);
		}

		if (updatedFilters.queryText) {
			params.set('search', updatedFilters.queryText);
		}

		if (updatedFilters.selectedCategories.length > 0) {
			params.set('category', updatedFilters.selectedCategories.join(','));
		}

		router.push(`${root}?${params.toString()}`, { scroll: false });
	};

	return (
		<Wrapper>
			<FilterWrapper>
				<StyledSelectInput
					aria-label="Filter by year"
					className="year-select"
					placeholder="Year"
					items={[ALL_YEARS_FILTER_RESET, ...getSelectableYears(items)]}
					selectedItem={filters.selectedYear}
					onChange={(year: IDropdownItem | null) =>
						updateFilters({
							selectedYear: year ? year.toString() : ALL_YEARS_FILTER_RESET,
						})
					}
				/>
				{type === 'news' && (
					<StyledMultiSelectInput
						aria-label="Filter by category"
						className="year-select"
						placeholder="Category"
						items={allCategoryLabels}
						onChange={(selectedItems: IDropdownItem[]) =>
							updateFilters({ selectedCategories: selectedItems })
						}
						initialSelectedItems={filters.selectedCategories}
						selectedItem={
							filters.selectedCategories.length === 1
								? filters.selectedCategories[0]
								: null
						}
					/>
				)}

				<SearchInput
					id="text-input"
					label="Search"
					value={searchValue}
					onClear={() => {
						updateFilters({ queryText: '' });
						setSearchValue('');
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter') updateFilters({ queryText: searchValue });
					}}
					onChange={(e) => setSearchValue(e.target.value)}
				/>
				<StyledArrowButton
					onClick={() => updateFilters({ queryText: searchValue })}
				>
					Search
				</StyledArrowButton>
			</FilterWrapper>

			<NewsroomOverviewList listData={listData} type={type} />

			{listData.length > 0 && (
				<NewsroomPagination
					pageNumber={pageNumber}
					totalPagesNumber={numberOfPages}
					root={root}
					queryString={searchParams.toString()}
				/>
			)}
		</Wrapper>
	);
}

const StyledSelectInput = styled(SelectInput)`
	${StyledSelectMain} {
		width: 100%;
	}
`;

const StyledMultiSelectInput = styled(MultiSelectInput)`
	${StyledSelectMain} {
		width: 100%;
	}
`;

const StyledArrowButton = styled(ArrowButton)`
	height: unset;
	line-height: normal;
	justify-content: center;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		width: 100%;
		margin-top: ${Css.pxToRem(8)};
	}
`;

const FilterWrapper = styled.div`
	display: flex;
	align-items: center;
	flex-direction: row;
	gap: ${Css.pxToRem(16)};
	background: ${Color.Neutral1};
	margin: ${Css.pxToRem(-160)} 0 ${Css.pxToRem(80)} 0;
	padding: ${Css.pxToRem(32)};
	position: relative;
	border: 1px solid ${Color.BorderCard};
	border-radius: ${Css.pxToRem(8)};

	.year-select {
		[type='button'] {
			min-width: ${Css.pxToRem(200)};
		}
	}
	#text-input {
		flex-grow: 1;
		color: unset; // Override onPrimary
	}

	@media (${contentWrapBreakpoint}) {
		margin-top: ${Css.pxToRem(-130)};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-top: ${Css.pxToRem(-180)};
		margin-bottom: ${Css.pxToRem(48)};
		flex-direction: column;
		padding: ${Css.pxToRem(24)};
		gap: ${Css.pxToRem(8)};

		.year-select {
			width: 100%;
		}
	}
`;

const Wrapper = styled(ContentWrap)`
	margin-top: ${Css.pxToRem(100)};

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		margin-top: ${Css.pxToRem(150)};
	}
`;
